import { useMemo, useRef } from 'react';
import type { CatalogEntry } from '../../../api/admin';
import { CATEGORY_LABEL } from '../ConnectionEditors';
import { BrandTile } from './BrandIcon';

/**
 * The rail (R6, screen 14): a searchable list of everything a workflow can be
 * made of, in two groups.
 *
 * SHAPE is how the flow is shaped — branch, rejoin, follow-up, reminders, an
 * ending — five forever, pinned above. DELIVER TO is the whole integration
 * catalogue, with its real count: Ben's reasoning for a permanent rail
 * rather than a dropdown is that the catalogue holds nineteen destinations
 * today and is meant to keep growing, and a menu that long is a menu nobody
 * reads to the bottom of. Each destination shows its category and a green dot
 * when the account already holds a credential for it.
 *
 * Clicking a tile adds the step after the selection (the canvas splices it
 * in where the edge rules allow — `insertNode`); dragging one onto the
 * surface lands it where it was dropped.
 */

export interface PaletteEntry {
  /** 'branch' / 'merge' / 'mint' / 'chase' / 'end' for a structural kind; otherwise a connection type. */
  key: string;
  label: string;
  glyph: string;
  /** the second line: what the shape does, or the destination's category */
  sub?: string;
  structural?: boolean;
  /** a destination the account already holds a credential for */
  connected?: boolean;
}

/** The shape-of-the-flow steps, which no catalogue will ever supply. */
const STRUCTURAL: PaletteEntry[] = [
  { key: 'branch', label: 'Branch on an answer', sub: 'Split into two paths', glyph: '?', structural: true },
  { key: 'merge', label: 'Paths rejoin', sub: 'Merge back together', glyph: '↳', structural: true },
  { key: 'mint', label: 'Follow-up form', sub: 'Send a second form', glyph: '🔗', structural: true },
  { key: 'chase', label: 'Reminders', sub: 'Nudge if unfinished', glyph: '⏰', structural: true },
  { key: 'end', label: 'Ending', sub: 'What they see after', glyph: '⚑', structural: true },
];

export default function PaletteRail({ catalog, search, onSearch, onAdd, onDragEntry, sheet, connectedTypes }: {
  catalog: CatalogEntry[];
  search: string;
  onSearch: (v: string) => void;
  /** Clicked: add it after the selection. */
  onAdd: (entry: PaletteEntry) => void;
  /** Dragged: the surface decides where it lands. */
  onDragEntry: (entry: PaletteEntry | null) => void;
  /** render for a bottom sheet rather than as the in-flow rail */
  sheet?: boolean;
  /** integration types the account holds a credential for — the green dot */
  connectedTypes?: Set<string>;
}) {
  const box = useRef<HTMLInputElement>(null);
  const hay = search.trim().toLowerCase();
  const matches = (text: string) => !hay || text.toLowerCase().includes(hay);

  const structural = STRUCTURAL.filter((e) => matches(`${e.label} ${e.sub || ''}`));
  // The catalogue calls the invite "Invite to continue", and it is the same
  // thing as the pinned "Follow-up form" above — same connection type, same
  // node, same everything. Two names for one step is a question somebody has
  // to stop and answer, so only the one the flow's own vocabulary uses stays.
  const deliverable = useMemo(() => catalog.filter((c) => c.type !== 'invite'), [catalog]);
  const destinations = useMemo(
    () => deliverable
      .filter((c) => matches(`${c.title} ${c.type} ${c.blurb || ''} ${CATEGORY_LABEL[c.category] || c.category || ''}`))
      .map((c): PaletteEntry => ({
        key: c.type,
        label: c.title,
        glyph: c.icon || '🔌',
        sub: CATEGORY_LABEL[c.category] || c.category || 'Integration',
        connected: !!connectedTypes?.has(c.type),
      })),
    [deliverable, hay, connectedTypes]
  );

  // A div rather than a button, deliberately: Chrome starts a drag on
  // mousedown for a draggable <button> and never fires its click, so the rail
  // could be dragged from but not clicked. Keyboard support is put back by
  // hand rather than lost with the element.
  const entry = (e: PaletteEntry) => (
    <div
      key={e.key}
      role="button"
      tabIndex={0}
      className={`wf-rail-entry${e.structural ? ' structural' : ''}`}
      draggable
      onDragStart={() => onDragEntry(e)}
      onDragEnd={() => onDragEntry(null)}
      onClick={() => onAdd(e)}
      onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onAdd(e); } }}
      title={e.structural ? `${e.label} — ${e.sub}` : `Add a ${e.label} step after the selected one`}
      aria-label={e.structural ? e.label : `Add a ${e.label} step`}
    >
      {e.structural
        ? <span className="glyph">{e.glyph}</span>
        : <BrandTile type={e.key} fallback={e.glyph} className="glyph" size={14} />}
      <span className="wf-rail-text">
        <span className="wf-rail-name">{e.label}</span>
        {e.sub && <span className="wf-rail-sub">{e.sub}{e.connected ? ' · connected' : ''}</span>}
      </span>
      {e.connected && <span className="wf-rail-dot" title="Connected — the account holds a credential for it" />}
    </div>
  );

  return (
    // `sheet` (§4H H3): the same list inside a bottom sheet rather than as a
    // 236px column — no border, no title of its own (the sheet has one), and
    // the rows are 44px. Nothing about the rail's own markup changes.
    <div className={sheet ? 'wf-rail wf-rail-sheet' : 'wf-rail'}>
      <input
        ref={box} className="fs-input wf-rail-search" placeholder="Add a step…" aria-label="Search steps"
        value={search} onChange={(ev) => onSearch(ev.target.value)}
      />
      {!!structural.length && <>
        <div className="wf-rail-group">Shape</div>
        <div className="wf-rail-list">{structural.map(entry)}</div>
      </>}
      <div className="wf-rail-group">Deliver to · {deliverable.length}</div>
      <div className="wf-rail-list grow">
        {destinations.length ? destinations.map(entry) : (
          <div className="wf-rail-empty">Nothing matches “{search}”.</div>
        )}
      </div>
      {!sheet && (
        <div className="wf-rail-note">
          Click a step to add it after the selected one. Drag steps to move them; drag the background to pan.
        </div>
      )}
    </div>
  );
}
